import { useEffect, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { debounce } from '../../utils/debounce';
import { IQueryPaginationParams } from './types';

export const useQueryPagination = <TData, TResponse>({
  queryFn,
  queryKey,
  serialize,
  limit,
  queryOptions = {},
  enabled = true,
}: IQueryPaginationParams<TData, TResponse>) => {
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [debounceSearch, setDebounceSearch] = useState('');

  const {
    data,
    isLoading: isLoadingQuery,
    isFetching,
    isRefetching,
    refetch,
    fetchNextPage: next,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    ...queryOptions,
    enabled,
    queryKey: [
      ...(Array.isArray(queryKey) ? queryKey : [queryKey]),
      debounceSearch?.trim(),
      limit,
    ],
    queryFn: async ({ pageParam }) =>
      await queryFn({ search: debounceSearch?.trim(), limit, skip: pageParam }),
    initialPageParam: 0,
    getNextPageParam: ({ cursor, count }) =>
      cursor < count ? cursor : undefined,
    select: (data) => serialize(data.pages.flatMap((page) => page.nodes)),
  });

  useEffect(() => {
    setIsLoading(isLoadingQuery);
  }, [isLoadingQuery]);

  const debounceSearchHandler = debounce((value: string) => {
    setIsLoading(false);
    setDebounceSearch(value);
  }, 350);

  const onSearch = (value: string) => {
    setIsLoading(true);
    setSearch(value);
    debounceSearchHandler(value);
  };

  const reset = () => {
    setSearch('');
    setIsLoading(false);
    setDebounceSearch('');
  };

  return {
    reset,
    data: data ?? [],
    onSearch,
    search,
    isLoading: isLoading || isLoadingQuery,
    next,
    hasNext: hasNextPage,
    isFetchingNextPage,
    isFetching,
  };
};
